import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import {
  ADDRESS_SEARCH_MIN_LENGTH,
  useSearchAddressesQuery,
} from "Api/Queries/Addresses/useSearchAddressesQuery";
import { useDebounce } from "Hooks/useDebounce";
import type { AddressSuggestionDto } from "Infrastructure/Api/Api";
import { useId, useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import {
  type FormattableAddress,
  getFormattedAddress,
} from "Utils/AddressUtils";

const RESOURCES = Resources.Components.Shared.AddressAutocomplete;
const INPUT_RESOURCES = Resources.Components.Shared.AddressInput;

type Props = {
  label: string;
  /**
   * Current address (from OCR / BE / a prior pick). Shown formatted while the
   * user isn't searching, and verified against the registry for the warning.
   */
  address?: FormattableAddress | null;
  onAcceptSuggestion: (suggestion: AddressSuggestionDto) => void;
  /** Called when the user clears the input — the parent clears its detail fields. */
  onClear?: () => void;
  /** Whether the address detail fields have validation errors — drives the error state. */
  hasErrors?: boolean;
  /** Reports whether the typed query is unapplied (diverges from the saved address). */
  onUncommittedChange?: (isUncommitted: boolean) => void;
  disabled?: boolean;
};

const normalize = (value?: string | null) => (value ?? "").trim().toLowerCase();

/**
 * Whether `address` is present in the registry results. We match on the
 * identifying parts (street + house number + municipality) and ignore PSČ /
 * orientation number so an otherwise-valid address isn't flagged just because
 * the PSČ is still missing.
 */
const isAddressInResults = (
  address: FormattableAddress,
  results: ReadonlyArray<AddressSuggestionDto>,
) =>
  results.some(
    (result) =>
      normalize(result.street) === normalize(address.street) &&
      normalize(result.streetNumber) === normalize(address.streetNumber) &&
      normalize(result.municipality) === normalize(address.municipality),
  );

/**
 * Whether the displayed address still equals the suggestion the user picked.
 * We compare the full field tuple (not a bare "was picked" flag) so the check
 * self-corrects when the parent swaps in a different address or the user edits
 * a detail field — either way it stops matching and verification resumes.
 */
const addressMatchesSuggestion = (
  address: FormattableAddress,
  suggestion: AddressSuggestionDto,
) =>
  normalize(address.street) === normalize(suggestion.street) &&
  normalize(address.streetNumber) === normalize(suggestion.streetNumber) &&
  normalize(address.orientationNumber) ===
    normalize(suggestion.orientationNumber) &&
  normalize(address.postalCode) === normalize(suggestion.postalCode) &&
  normalize(address.municipality) === normalize(suggestion.municipality);

/**
 * Address registry search, fully decoupled from the detail fields.
 *
 * - Displays the current formatted address until the user starts typing.
 * - While typing, queries the GeoApi (RUIAN) proxy and offers suggestions.
 * - Picking a suggestion only calls `onAcceptSuggestion`; the parent maps it
 *   onto its own form fields (the detail fields remain the source of truth).
 * - The input surfaces its status as an icon + helper text below it: error
 *   (red, when the address fields are invalid), or amber warning when the typed
 *   query hasn't been applied (no suggestion picked) or the displayed address
 *   isn't in the registry. A matched, applied address shows nothing.
 */
export const AddressSearchAutocomplete = ({
  label,
  address,
  onAcceptSuggestion,
  onClear,
  hasErrors,
  onUncommittedChange,
  disabled,
}: Props) => {
  const { t } = useResource();
  // `null` = not editing → show the formatted address. A string = the user's
  // in-progress search query.
  const inputId = useId();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  // The suggestion the user picked this session. A picked address came straight
  // from the registry, so it's trivially verified — we trust it instead of
  // re-searching (which is fragile, e.g. for street-less villages).
  const [acceptedSuggestion, setAcceptedSuggestion] =
    useState<AddressSuggestionDto | null>(null);
  const debouncedQuery = useDebounce(searchText ?? "", 300);

  const safeAddress = address ?? {};
  const formattedAddress = getFormattedAddress(safeAddress);
  const isEditing = searchText !== null;
  const inputValue = searchText ?? formattedAddress;

  const {
    data: suggestions = [],
    isFetching,
    isError,
  } = useSearchAddressesQuery({ query: debouncedQuery });

  // A picked address is verified by trust — no re-search needed.
  const isVerifiedByPick = Boolean(
    acceptedSuggestion &&
      addressMatchesSuggestion(safeAddress, acceptedSuggestion),
  );

  // Verify a prefilled address (OCR / BE) against the registry while the user
  // isn't actively searching. Needs a municipality plus a street or house number
  // to look anything up. Skipped entirely once the address is verified by pick.
  const hasLookupContext = Boolean(
    normalize(safeAddress.municipality) &&
      (normalize(safeAddress.street) || normalize(safeAddress.streetNumber)),
  );
  const shouldVerify = !isEditing && !isVerifiedByPick && hasLookupContext;
  // For street-less villages the registry returns the municipality name as the
  // street too (a "virtual street"), so street === municipality. Repeating that
  // token makes the GeoApi fulltext return nothing, so drop the redundant street.
  const isVirtualStreet =
    normalize(safeAddress.street) === normalize(safeAddress.municipality);
  const verificationQuery = [
    isVirtualStreet ? null : safeAddress.street,
    safeAddress.streetNumber,
    safeAddress.municipality,
  ]
    .filter(Boolean)
    .join(" ");
  const {
    data: verificationResults = [],
    isFetching: isVerifying,
    isError: isVerificationError,
  } = useSearchAddressesQuery({
    query: shouldVerify ? verificationQuery : "",
  });

  const isVerified =
    shouldVerify && !isVerifying && !isVerificationError && !hasErrors;
  const isAddressUnverified =
    isVerified && !isAddressInResults(safeAddress, verificationResults);

  // The visible query was typed but not applied (no suggestion picked) — it
  // diverges from the saved address, so warn that typing alone won't change it.
  // Only once the field is blurred, to avoid warning prematurely while typing.
  // The typed query diverges from the saved address (regardless of focus).
  const isQueryDiverged =
    isEditing && normalize(searchText ?? "") !== normalize(formattedAddress);
  // Only surface the warning once blurred, to avoid warning prematurely while typing.
  const isQueryUncommitted = !isFocused && isQueryDiverged;

  // While editing, the committed-address statuses (error / not-in-registry) are
  // stale, so the uncommitted-query hint takes over. A matched address shows
  // nothing.
  const status = isQueryUncommitted
    ? "uncommitted"
    : hasErrors && !isEditing
      ? "error"
      : isAddressUnverified
        ? "warning"
        : "none";

  // An uncommitted (typed-but-not-applied) query is treated as an error too.
  const isErrorStatus = status === "error" || status === "uncommitted";

  const statusIcon = isErrorStatus ? (
    <CancelRoundedIcon color="error" fontSize="small" />
  ) : status === "warning" ? (
    <WarningAmberRoundedIcon sx={{ color: "warning.dark" }} fontSize="small" />
  ) : undefined;

  const helperText =
    status === "error" ? (
      t(INPUT_RESOURCES.HasErrors)
    ) : status === "uncommitted" ? (
      t(INPUT_RESOURCES.UncommittedQuery)
    ) : status === "warning" ? (
      <Box component="span" sx={{ color: "warning.dark" }}>
        {t(INPUT_RESOURCES.NotInRegistry)}
      </Box>
    ) : (
      " "
    );

  const noOptionsText = isError
    ? t(RESOURCES.Error)
    : debouncedQuery.length < ADDRESS_SEARCH_MIN_LENGTH
      ? t(RESOURCES.MinLength)
      : t(RESOURCES.NoResults);

  return (
    <Box>
      {label && (
        <Typography component="label" htmlFor={inputId} fontWeight={400}>
          {label}
        </Typography>
      )}
      <Autocomplete
        id={inputId}
        freeSolo
        options={suggestions}
        loading={isFetching}
        disabled={disabled}
        fullWidth
        filterOptions={(x) => x}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : getFormattedAddress(option)
        }
        value={null}
        inputValue={inputValue}
        onInputChange={(_, newValue, reason) => {
          if (reason === "clear") {
            onClear?.();
            // The cleared fields no longer match any pick.
            setAcceptedSuggestion(null);
            // Drop back to "not editing" (null, not "") so the now-empty address
            // is treated as the committed value: if its detail fields are required
            // it fails validation and the input shows the error state — rather than
            // staying in an editing state that suppresses the error.
            setSearchText(null);
            // Clearing empties the detail fields too — nothing left unapplied.
            onUncommittedChange?.(false);
            return;
          }
          if (reason === "input") {
            setSearchText(newValue);
            onUncommittedChange?.(
              normalize(newValue) !== normalize(formattedAddress),
            );
          }
        }}
        onChange={(_, selected) => {
          if (selected && typeof selected !== "string") {
            onAcceptSuggestion(selected);
            // Remember the pick so verification trusts it instead of re-searching.
            setAcceptedSuggestion(selected);
            // Drop back to showing the (now updated) formatted address.
            setSearchText(null);
            onUncommittedChange?.(false);
          }
        }}
        noOptionsText={noOptionsText}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            error={isErrorStatus}
            helperText={helperText}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isErrorStatus ? "error.main" : "divider",
                borderRadius: 1,
              },
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: statusIcon,
                endAdornment: (
                  <>
                    {isFetching ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={({ key, ...rest }, option) => {
          const optionLabel =
            typeof option === "string" ? option : getFormattedAddress(option);
          const optionKey =
            typeof option === "string"
              ? `free-${option}`
              : option.addressCode || optionLabel;
          return (
            <Box component="li" {...rest} key={optionKey}>
              <Typography>{optionLabel}</Typography>
            </Box>
          );
        }}
      />
    </Box>
  );
};
